/**
 * svgAxonometric — geometry helpers for the axonometric (render_360) viewer.
 *
 * Analogous to `svgInsolation.js` and `svgArrange.js`; contains product-domain
 * logic that depends on `compartmentsDictionary` (compartments.json) type data.
 */

import {
  getClosestAngleIndex,
  getCompartmentDominantVectorAngle,
} from '../../../shared/utils/geometryUtils.js'

/**
 * getIsometricAttractiveFrame — returns the frame index that best showcases
 * the product in the axonometric (render_360) viewer.
 *
 * **Selection priority:**
 *   1. Largest OutdoorSpace (non-technical) compartment on `refTier`
 *      (balcony, terrace, garden — characteristic of apartment exposure)
 *   2. Largest indoor (non-OutdoorSpace) compartment on `refTier`
 *   3. `fallbackAzimuth` when no compartment can be resolved or the SVG path
 *      is missing
 *
 * **En-face avoidance:**
 * Even frame indices (0, 2, 4, …) correspond to cardinal frontal views (camera
 * looking directly N/E/S/W), which appear flat. Odd indices are diagonal views
 * — visually richer and architecturally more interesting.
 * When the best index is even, the function advances it by 1 (modulo
 * `framesCount`).
 *
 * En-face avoidance is **not** applied in the no-winner case (pure azimuth
 * fallback), because without any spatial compartment information we cannot know
 * whether the adjacent diagonal frame shows the product better.
 *
 * **Requires a live or cloned SVG DOM element** — uses `querySelector` to
 * locate compartment paths by their `number` attribute.
 *
 * @param {SVGSVGElement|null} svgElement            — product 2D plan SVG element
 * @param {object[]}           productCompartments   — `product.compartments` array
 * @param {object|null}        compartmentsDictionary — raw `compartments.json` or
 *   its `compartments_types` map; accepts either shape (follows the same
 *   normalisation as `summaryItems.js` / `svgColorScheme.js`)
 * @param {number}             refTier               — current floor/tier number
 * @param {number}             framesCount           — total render_360 frames (e.g. 8)
 * @param {number}             fallbackAzimuth       — `product.azimuth`, used when
 *   compartment-based analysis yields no result
 * @returns {number} frame index in [0, framesCount)
 */
export function getIsometricAttractiveFrame(
  svgElement,
  productCompartments,
  compartmentsDictionary,
  refTier,
  framesCount,
  fallbackAzimuth,
) {
  if (!framesCount) return 0

  // Camera orbits CW from above when index increases. Frame 0 is at 0°, each
  // subsequent frame adds 360/framesCount degrees.
  const isometricAngles = Array.from(
    { length: framesCount },
    (_, i) => (i * 360) / framesCount,
  )

  // Accept both the raw compartments.json (has .compartments_types wrapper) and
  // the unwrapped map directly — same normalisation used across the codebase.
  const types = compartmentsDictionary?.compartments_types ?? compartmentsDictionary ?? {}

  // Filter to the current floor/tier. Loose equality handles the case where
  // ref_tier may arrive as string (data) vs number (runtime).
  const tierCompartments = Array.isArray(productCompartments)
    // eslint-disable-next-line eqeqeq
    ? productCompartments.filter((c) => c?.ref_tier == refTier)
    : []

  // Outdoor compartments: group === 'OutdoorSpace', not marked technical.
  // technical: true entries are structural/utility types (e.g. rooftop access)
  // that should not drive the attractive-view selection.
  const outdoorCompartments = tierCompartments.filter(
    (c) => types[c?.name_cad]?.group === 'OutdoorSpace' && !types[c?.name_cad]?.technical,
  )

  // Indoor compartments: everything that is not OutdoorSpace.
  const indoorCompartments = tierCompartments.filter(
    (c) => types[c?.name_cad]?.group !== 'OutdoorSpace',
  )

  // Returns the compartment with the highest `area_original`, or null when the
  // array is empty.
  const largestByArea = (arr) =>
    arr.length === 0
      ? null
      : arr.reduce((best, c) =>
          (Number(c?.area_original) || 0) > (Number(best?.area_original) || 0) ? c : best,
        )

  // Winner: prefer the largest outdoor compartment; fall back to largest indoor.
  const winner = largestByArea(outdoorCompartments) ?? largestByArea(indoorCompartments)

  // ── No winner ─────────────────────────────────────────────────────────────
  // No compartments found on this tier (or empty product) → pure azimuth
  // fallback without en-face avoidance (no spatial context available).
  if (!winner) {
    return getClosestAngleIndex(Number(fallbackAzimuth) || 0, isometricAngles)
  }

  // ── Winner found → SVG-based dominant direction ───────────────────────────
  // Compute the direction from the plan's viewBox centre towards the mass of
  // the winner compartment's boundary polygon.
  const dominantAngle = getCompartmentDominantVectorAngle(svgElement, winner.number)

  // If SVG path lookup fails (no DOM, missing path, empty d attribute), fall
  // back to the product azimuth — but still apply en-face avoidance since we
  // know a spatially-significant compartment exists.
  const targetAngle = dominantAngle !== null
    ? dominantAngle
    : (Number(fallbackAzimuth) || 0)

  // ── Closest frame → en-face avoidance ────────────────────────────────────
  let bestIndex = getClosestAngleIndex(targetAngle, isometricAngles)

  // Even indices are cardinal (frontal) views — advance to the next diagonal.
  if (bestIndex % 2 === 0) bestIndex = (bestIndex + 1) % framesCount

  return bestIndex
}
